import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, TrendingUp, ShieldCheck, Building2, Landmark, FileText, UserCheck,
  Sparkles, CheckCircle2, MapPin, Mail, Phone, Calendar, ChevronDown,
  Globe, Wallet, LineChart, ArrowLeft, Home, BadgeCheck, Users, Briefcase,
} from "lucide-react";
import { SITE } from "@/config";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#D9A441";
const TEAL = "#5EC8BE";

const CURRENCIES = {
  INR: { symbol: "₹", locale: "en-IN", name: "Indian Rupee" },
  USD: { symbol: "$", locale: "en-US", name: "US Dollar" },
  EUR: { symbol: "€", locale: "de-DE", name: "Euro" },
  GBP: { symbol: "£", locale: "en-GB", name: "British Pound" },
  AED: { symbol: "د.إ ", locale: "en-AE", name: "UAE Dirham" },
  SGD: { symbol: "S$", locale: "en-SG", name: "Singapore Dollar" },
  CAD: { symbol: "C$", locale: "en-CA", name: "Canadian Dollar" },
  AUD: { symbol: "A$", locale: "en-AU", name: "Australian Dollar" },
  CHF: { symbol: "CHF ", locale: "de-CH", name: "Swiss Franc" },
  JPY: { symbol: "¥", locale: "ja-JP", name: "Japanese Yen" },
};
const EURO = ["DE", "FR", "ES", "IT", "NL", "IE", "PT", "AT", "BE", "FI", "GR", "LU"];
const COUNTRY_CCY = { IN: "INR", US: "USD", GB: "GBP", AE: "AED", SG: "SGD", CA: "CAD", AU: "AUD", CH: "CHF", JP: "JPY" };

const MIN_INR = 2500000;
const SLIDER_MAX = 500000000; // ₹50 Cr (manual input has no upper limit)

/* ---------------- helpers ---------------- */
const formatINR = (n) => {
  if (!isFinite(n)) return "₹0";
  if (n >= 1e7) return `₹${(n / 1e7).toLocaleString("en-IN", { maximumFractionDigits: 2 })} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toLocaleString("en-IN", { maximumFractionDigits: 2 })} Lakh`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
};
const formatCcy = (n, cur) => {
  const c = CURRENCIES[cur] || CURRENCIES.INR;
  if (cur === "INR") return formatINR(n);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${c.symbol}${(n / 1e9).toLocaleString(c.locale, { maximumFractionDigits: 2 })}B`;
  if (abs >= 1e6) return `${c.symbol}${(n / 1e6).toLocaleString(c.locale, { maximumFractionDigits: 2 })}M`;
  return `${c.symbol}${Math.round(n).toLocaleString(c.locale)}`;
};
const preciseINR = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

/* ---------------- primitives ---------------- */
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Overline = ({ children, color = ACCENT }) => (
  <p className="text-xs uppercase tracking-[0.28em] text-neutral-500 mb-6 flex items-center gap-3">
    <span className="h-px w-8" style={{ background: color }} />
    {children}
  </p>
);

/* ---------------- top bar ---------------- */
const InvestNav = () => (
  <nav data-testid="invest-nav" className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-black/40 border-b border-white/5">
    <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
      <Link to="/" data-testid="invest-logo" className="text-sm font-semibold tracking-tight whitespace-nowrap">Anubhav Sahni</Link>
      <div className="flex items-center gap-2 sm:gap-3">
        <Link to="/" data-testid="invest-home-link" className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white transition-colors duration-300 px-2">
          <Home size={15} /> <span className="hidden sm:inline">Home</span>
        </Link>
        <a href="#planner" data-testid="invest-nav-planner" className="hidden md:inline text-sm text-neutral-400 hover:text-white transition-colors duration-300 px-2">Planner</a>
        <a href="#final-cta" data-testid="invest-nav-consult" className="inline-flex items-center gap-1.5 rounded-full bg-white text-black text-sm font-medium px-4 py-2 hover:bg-[#D9A441] transition-colors duration-300 whitespace-nowrap">
          Book Consultation
        </a>
      </div>
    </div>
  </nav>
);

/* ---------------- hero ---------------- */
const Hero = () => (
  <section id="invest-top" data-testid="invest-hero" className="relative min-h-screen flex items-center overflow-hidden">
    <img
      src="https://images.unsplash.com/photo-1513061379709-ef0cd1695189?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBkYXJrJTIwY2l0eSUyMHNreWxpbmUlMjBuaWdodHxlbnwwfHx8fDE3ODYyMjc1MjF8MA&ixlib=rb-4.1.0&q=85"
      alt="Luxury city skyline at night"
      className="absolute inset-0 w-full h-full object-cover opacity-40"
      loading="eager"
    />
    <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(8,8,10,0.55) 0%, rgba(8,8,10,0.85) 55%, rgba(8,8,10,1) 100%)" }} />
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {[["18%", "22%", 0], ["70%", "30%", 1.2], ["44%", "68%", 0.6], ["82%", "72%", 1.8]].map(([l, t, d], i) => (
        <motion.span
          key={i}
          className="absolute h-1 w-1 rounded-full"
          style={{ left: l, top: t, background: ACCENT, boxShadow: `0 0 12px ${ACCENT}` }}
          animate={{ opacity: [0.15, 0.7, 0.15], y: [0, -14, 0] }}
          transition={{ duration: 5 + i, repeat: Infinity, delay: d, ease: "easeInOut" }}
        />
      ))}
    </div>

    <div className="relative max-w-6xl mx-auto px-5 md:px-8 w-full pt-24">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
        <span className="inline-flex items-center gap-2 rounded-full border border-[#D9A441]/30 bg-[#D9A441]/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-[#D9A441] font-semibold">
          <Landmark size={13} /> Private Real Estate Investment
        </span>
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
        className="font-serif text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] mt-8 max-w-4xl"
      >
        Invest Beyond Traditional Savings.<br />
        <span className="text-neutral-400">Build Long-Term Wealth Through Real Estate.</span>
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.25 }}
        className="mt-8 text-lg md:text-xl text-neutral-400 leading-relaxed max-w-2xl"
      >
        Explore professionally structured real estate investment opportunities associated with established Indian real estate developers. Whether you&apos;re investing ₹25 Lakhs or ₹25 Crores+, discover opportunities designed for investors seeking regular income potential and long-term wealth creation.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-11 flex flex-col sm:flex-row gap-3"
      >
        <a href="#planner" data-testid="hero-plan-btn" className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-8 py-4 hover:bg-[#D9A441] transition-colors duration-300">
          <LineChart size={17} /> Plan Your Investment
        </a>
        <a href="#final-cta" data-testid="hero-consult-btn" className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full border border-white/15 text-sm font-semibold px-8 py-4 text-neutral-100 hover:bg-white/5 hover:border-white/30 transition-colors duration-300">
          <Calendar size={17} /> Book Private Consultation
        </a>
      </motion.div>
    </div>
  </section>
);

/* ---------------- trust ---------------- */
const TRUST = [
  { icon: Building2, t: "Established Since 1987", d: "A legacy spanning nearly four decades." },
  { icon: MapPin, t: "Operations Across India", d: "Presence across multiple Indian cities." },
  { icon: Landmark, t: "Listed on NSE & BSE", d: "A publicly listed development company." },
  { icon: BadgeCheck, t: "38+ Years of Experience", d: "Extensive real estate development experience." },
  { icon: FileText, t: "Professional Documentation", d: "Structured, transparent investment paperwork." },
  { icon: UserCheck, t: "Dedicated Relationship Manager", d: "Personalized guidance for every investor." },
];
const Trust = () => (
  <section data-testid="invest-trust" className="border-t border-white/5">
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-24 md:py-32">
      <Reveal><Overline>The Developer</Overline></Reveal>
      <Reveal delay={0.05}>
        <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight max-w-2xl">Backed by an established, exchange-listed developer.</h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-14">
        {TRUST.map((c, i) => {
          const Icon = c.icon;
          return (
            <Reveal key={c.t} delay={i * 0.05}>
              <div className="group h-full rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent p-7 hover:-translate-y-1 hover:border-white/20 transition-all duration-500">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03]" style={{ color: ACCENT }}>
                  <Icon size={20} strokeWidth={1.5} />
                </span>
                <h3 className="text-lg font-semibold mt-6">{c.t}</h3>
                <p className="text-neutral-400 mt-2 leading-relaxed text-sm">{c.d}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={0.1}>
        <p className="mt-8 text-sm text-neutral-500 max-w-2xl">Investment opportunities are discussed during consultation along with documentation, investment structure and applicable terms.</p>
      </Reveal>
    </div>
  </section>
);

/* ---------------- planner ---------------- */
const ResultCard = ({ label, value, sub, accent }) => (
  <motion.div
    layout
    className="rounded-2xl border border-white/10 bg-[#08080A]/60 backdrop-blur-md p-5"
  >
    <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">{label}</p>
    <p className="text-2xl md:text-[26px] font-semibold mt-2 tracking-tight" style={accent ? { color: ACCENT } : {}}>{value}</p>
    {sub && <p className="text-xs text-neutral-500 mt-1">{sub}</p>}
  </motion.div>
);

const Planner = ({ rate, rates, currency, setCurrency, amountInr, setAmountInr, duration, setDuration, onContinue }) => {
  const cur = CURRENCIES[currency] || CURRENCIES.INR;
  const fx = (rates && rates[currency]) || 1;
  const amountCur = amountInr * fx;

  const monthlyInr = (amountInr * rate) / 100 / 12;
  const annualInr = (amountInr * rate) / 100;
  const totalInr = annualInr * (duration / 12);

  const [rawInput, setRawInput] = useState("");
  useEffect(() => { setRawInput(""); }, [currency]);

  const onManual = (e) => {
    const digits = e.target.value.replace(/[^0-9.]/g, "");
    setRawInput(e.target.value);
    const v = parseFloat(digits);
    if (isFinite(v) && v > 0) setAmountInr(v / fx);
  };
  const displayInput = rawInput !== "" ? rawInput : Math.round(amountCur).toLocaleString(cur.locale);

  return (
    <section id="planner" data-testid="invest-planner" className="border-t border-white/5">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-24 md:py-32">
        <Reveal><Overline color={TEAL}>Investment Planner</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight max-w-3xl">Plan your investment. See illustrative estimates instantly.</h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start mt-14">
          {/* controls */}
          <Reveal className="lg:col-span-5">
            <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent p-6 md:p-8 space-y-9">
              {/* currency */}
              <div>
                <label className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-neutral-500 mb-3"><Globe size={13} /> Currency</label>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(CURRENCIES).map((c) => {
                    const active = c === currency;
                    return (
                      <button
                        key={c}
                        type="button"
                        data-testid={`ccy-${c}`}
                        onClick={() => setCurrency(c)}
                        className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors duration-300 ${active ? "bg-white text-black border-transparent" : "text-neutral-300 border-white/10 hover:border-white/30"}`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* amount */}
              <div>
                <label className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-neutral-500 mb-3"><Wallet size={13} /> Investment Amount</label>
                <div className="flex items-center gap-2 border-b border-white/15 focus-within:border-[#D9A441] transition-colors">
                  <span className="text-2xl text-neutral-500">{cur.symbol.trim()}</span>
                  <input
                    data-testid="planner-amount-input"
                    inputMode="decimal"
                    value={displayInput}
                    onChange={onManual}
                    onBlur={() => setRawInput("")}
                    className="w-full bg-transparent py-2.5 text-2xl font-semibold outline-none placeholder:text-neutral-600"
                  />
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-neutral-500">
                  <span>Min {formatINR(MIN_INR)}</span>
                  <span>≈ {preciseINR(amountInr)}</span>
                </div>
                <input
                  type="range"
                  data-testid="planner-amount-slider"
                  min={MIN_INR}
                  max={SLIDER_MAX}
                  step={500000}
                  value={Math.min(Math.max(amountInr, MIN_INR), SLIDER_MAX)}
                  onChange={(e) => setAmountInr(parseFloat(e.target.value))}
                  className="invest-range w-full mt-4"
                />
                <div className="flex justify-between text-[11px] text-neutral-600 mt-1"><span>₹25 L</span><span>₹50 Cr+</span></div>
              </div>

              {/* duration */}
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-3 block">Duration</label>
                <div className="grid grid-cols-3 gap-2">
                  {[12, 18, 24].map((d) => {
                    const active = d === duration;
                    return (
                      <button
                        key={d}
                        type="button"
                        data-testid={`duration-${d}`}
                        onClick={() => setDuration(d)}
                        className={`rounded-xl py-3 text-sm border transition-colors duration-300 ${active ? "bg-white text-black border-transparent font-semibold" : "text-neutral-300 border-white/10 hover:border-white/30"}`}
                      >
                        {d} Months
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3">
                <span className="text-sm text-neutral-400">Illustrative Annual Rate</span>
                <span className="text-lg font-semibold" style={{ color: ACCENT }} data-testid="planner-rate">{rate}%</span>
              </div>
            </div>
          </Reveal>

          {/* results */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="grid sm:grid-cols-2 gap-4">
                <ResultCard label="Investment Amount" value={formatCcy(amountCur, currency)} sub={CURRENCIES[currency].name} accent />
                <ResultCard label="Equivalent INR" value={formatINR(amountInr)} sub={preciseINR(amountInr)} />
                <ResultCard label="Illustrative Monthly Income" value={preciseINR(monthlyInr)} sub={`≈ ${formatCcy(monthlyInr * fx, currency)}`} />
                <ResultCard label="Illustrative Annual Income" value={preciseINR(annualInr)} sub={`≈ ${formatCcy(annualInr * fx, currency)}`} />
                <ResultCard label="Illustrative Total Income" value={preciseINR(totalInr)} sub={`over ${duration} months`} />
                <ResultCard label="Equivalent (selected)" value={formatCcy(amountCur, currency)} sub={`1 INR ≈ ${fx.toFixed(4)} ${currency}`} />
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div data-testid="planner-disclaimer" className="mt-6 rounded-xl border border-[#D9A441]/25 bg-[#D9A441]/[0.06] px-4 py-3.5 max-w-2xl">
                <p className="text-xs text-neutral-300 leading-relaxed">
                  <span className="font-semibold text-[#D9A441]">Illustrative calculation only.</span> The displayed figures are estimates based on the assumptions selected and do not constitute a guarantee, assured return, investment advice or an offer to accept funds. Actual terms and outcomes depend on the specific opportunity and its documentation.
                </p>
              </div>
              <button
                data-testid="planner-continue-btn"
                onClick={onContinue}
                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-8 py-4 hover:bg-[#D9A441] transition-colors duration-300"
              >
                Continue <ArrowRight size={17} className="group-hover:translate-x-0.5 transition-transform duration-300" />
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- enquiry ---------------- */
const OBJECTIVES = ["Regular Income", "Long-Term Wealth Creation", "Portfolio Diversification", "NRI Investment", "Corporate Investment", "Other"];
const PROFILES = ["First-Time Investor", "Experienced Investor", "High Net Worth Individual", "Institutional"];
const CONTACTS = ["Phone", "Zoom", "Office Meeting"];

const Enquiry = ({ open, planner, rates, rate, onDone }) => {
  const fx = (rates && rates[planner.currency]) || 1;
  const amountCur = planner.amountInr * fx;
  const monthlyInr = (planner.amountInr * rate) / 100 / 12;
  const annualInr = (planner.amountInr * rate) / 100;
  const totalInr = annualInr * (planner.duration / 12);

  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", country: "", city: "",
    investment_objective: OBJECTIVES[0], investor_profile: PROFILES[0], preferred_contact: CONTACTS[0], message: "",
  });
  const [agree1, setAgree1] = useState(false);
  const [agree2, setAgree2] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!agree1 || !agree2) { toast.error("Please accept both acknowledgements to continue."); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        currency: planner.currency,
        amount_currency: Math.round(amountCur),
        amount_inr: Math.round(planner.amountInr),
        duration_months: planner.duration,
        annual_rate_pct: rate,
        monthly_income_inr: Math.round(monthlyInr),
        annual_income_inr: Math.round(annualInr),
        total_income_inr: Math.round(totalInr),
      };
      const { data } = await axios.post(`${API}/invest/enquiry`, payload);
      toast.success("Investment enquiry submitted.");
      onDone({ reference: data.reference, name: form.full_name, amountCur, currency: planner.currency, monthlyInr, annualInr, duration: planner.duration });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquiry" data-testid="invest-enquiry" className="border-t border-white/5">
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-24 md:py-32">
        <Reveal><Overline>Investment Enquiry</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight">Request your investment proposal.</h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-5 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><p className="text-neutral-500 text-xs">Amount</p><p className="font-semibold mt-1" style={{ color: ACCENT }}>{formatCcy(amountCur, planner.currency)}</p></div>
            <div><p className="text-neutral-500 text-xs">Monthly</p><p className="font-semibold mt-1">{preciseINR(monthlyInr)}</p></div>
            <div><p className="text-neutral-500 text-xs">Annual</p><p className="font-semibold mt-1">{preciseINR(annualInr)}</p></div>
            <div><p className="text-neutral-500 text-xs">Duration</p><p className="font-semibold mt-1">{planner.duration} mo</p></div>
          </div>
        </Reveal>

        <form onSubmit={submit} data-testid="invest-enquiry-form" className="mt-10 space-y-8">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
            {[
              { n: "full_name", l: "Full Name", t: "text" },
              { n: "email", l: "Email", t: "email" },
              { n: "phone", l: "Phone Number", t: "tel" },
              { n: "country", l: "Country", t: "text" },
              { n: "city", l: "City", t: "text" },
            ].map((f) => (
              <div key={f.n}>
                <label className="block text-xs uppercase tracking-[0.16em] text-neutral-500 mb-2">{f.l}</label>
                <input
                  data-testid={`enq-${f.n}`}
                  name={f.n} type={f.t} required={f.n !== "city"} value={form[f.n]} onChange={update}
                  className="w-full bg-transparent border-b border-white/20 py-3 text-lg outline-none focus:border-[#D9A441] transition-colors placeholder:text-neutral-600"
                />
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
            {[
              { n: "investment_objective", l: "Investment Objective", opts: OBJECTIVES },
              { n: "investor_profile", l: "Investor Profile", opts: PROFILES },
              { n: "preferred_contact", l: "Preferred Contact", opts: CONTACTS },
            ].map((f) => (
              <div key={f.n}>
                <label className="block text-xs uppercase tracking-[0.16em] text-neutral-500 mb-2">{f.l}</label>
                <select
                  data-testid={`enq-${f.n}`}
                  name={f.n} value={form[f.n]} onChange={update}
                  className="w-full bg-transparent border-b border-white/20 py-3 text-lg outline-none focus:border-[#D9A441] transition-colors [&>option]:bg-[#0b0b0d]"
                >
                  {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.16em] text-neutral-500 mb-2">Message</label>
            <textarea data-testid="enq-message" name="message" rows={3} value={form.message} onChange={update}
              className="w-full bg-transparent border-b border-white/20 py-3 text-lg outline-none focus:border-[#D9A441] transition-colors resize-none placeholder:text-neutral-600" />
          </div>

          <div className="space-y-4 pt-2">
            {[
              { s: agree1, set: setAgree1, id: "agree-illustrative", label: "I understand the Investment Planner provides illustrative estimates only." },
              { s: agree2, set: setAgree2, id: "agree-privacy", node: (<>I agree to the <a href="/invest/privacy" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-neutral-200 underline underline-offset-2 hover:text-white">Privacy Policy</a> and <a href="/invest/terms" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-neutral-200 underline underline-offset-2 hover:text-white">Terms &amp; Conditions</a>.</>) },
            ].map((c) => (
              <label key={c.id} data-testid={c.id} onClick={() => c.set(!c.s)} className="flex items-start gap-3 cursor-pointer group">
                <span className={`mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded border transition-colors ${c.s ? "bg-[#D9A441] border-[#D9A441]" : "border-white/25 group-hover:border-white/50"}`}>
                  {c.s && <CheckCircle2 size={13} className="text-black" />}
                </span>
                <span className="text-sm text-neutral-400 leading-relaxed">{c.node || c.label}</span>
              </label>
            ))}
          </div>

          <button type="submit" disabled={loading} data-testid="enq-submit-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-8 py-4 hover:bg-[#D9A441] transition-colors duration-300 disabled:opacity-50">
            {loading ? "Submitting…" : "Request My Investment Proposal"}
            {!loading && <ArrowRight size={17} className="group-hover:translate-x-0.5 transition-transform duration-300" />}
          </button>
        </form>
      </div>
    </section>
  );
};

/* ---------------- thank you ---------------- */
const ThankYou = ({ result }) => (
  <section data-testid="invest-thankyou" className="border-t border-white/5">
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-28 md:py-40 text-center">
      <Reveal>
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full text-black" style={{ background: ACCENT }}>
          <CheckCircle2 size={28} />
        </span>
        <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-8">Thank you, {result.name.split(" ")[0] || "there"}.</h2>
        <p className="text-neutral-400 mt-4 max-w-lg mx-auto">Your investment enquiry has been received. An advisor will reach out to discuss opportunities aligned with your goals.</p>
      </Reveal>
      <Reveal delay={0.05}>
        <div className="mt-10 rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent p-8 text-left">
          <div className="grid grid-cols-2 gap-6">
            <div><p className="text-xs uppercase tracking-[0.16em] text-neutral-500">Investment Amount</p><p className="text-xl font-semibold mt-1" style={{ color: ACCENT }}>{formatCcy(result.amountCur, result.currency)}</p></div>
            <div><p className="text-xs uppercase tracking-[0.16em] text-neutral-500">Illustrative Monthly</p><p className="text-xl font-semibold mt-1">{preciseINR(result.monthlyInr)}</p></div>
            <div><p className="text-xs uppercase tracking-[0.16em] text-neutral-500">Illustrative Annual</p><p className="text-xl font-semibold mt-1">{preciseINR(result.annualInr)}</p></div>
            <div><p className="text-xs uppercase tracking-[0.16em] text-neutral-500">Duration</p><p className="text-xl font-semibold mt-1">{result.duration} months</p></div>
          </div>
          <div className="mt-6 border-t border-white/10 pt-5">
            <p className="text-xs uppercase tracking-[0.16em] text-neutral-500">Reference Number</p>
            <p className="text-2xl font-semibold mt-1 tracking-tight" data-testid="thankyou-reference">{result.reference}</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
          <a href="#final-cta" data-testid="thankyou-consult" className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-8 py-4 hover:bg-[#D9A441] transition-colors duration-300">
            <Calendar size={16} /> Book Private Consultation
          </a>
          <Link to="/" data-testid="thankyou-home" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 text-sm font-semibold px-8 py-4 hover:bg-white/5 transition-colors duration-300">
            <Home size={16} /> Return Home
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- how it works ---------------- */
const STEPS = ["Plan Your Investment", "Submit Investment Enquiry", "Investment Review", "Private Consultation", "Documentation", "Investment Process", "Ongoing Support"];
const HowItWorks = () => (
  <section data-testid="invest-how" className="border-t border-white/5">
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-24 md:py-32">
      <Reveal><Overline color={TEAL}>How It Works</Overline></Reveal>
      <Reveal delay={0.05}><h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight">A clear path from planning to ongoing support.</h2></Reveal>
      <div className="mt-14 relative">
        <div className="absolute left-[15px] top-2 bottom-2 w-px bg-white/10" />
        <div className="space-y-8">
          {STEPS.map((s, i) => (
            <Reveal key={s} delay={i * 0.05}>
              <div className="flex items-center gap-5">
                <span className="relative z-10 flex-none inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-[#08080A] text-xs font-semibold" style={{ color: ACCENT }}>{i + 1}</span>
                <p className="text-lg md:text-xl">{s}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- why / who ---------------- */
const WHY = [
  { icon: ShieldCheck, t: "Professionally Structured Opportunities" },
  { icon: Building2, t: "Established Indian Developers" },
  { icon: FileText, t: "Transparent Documentation" },
  { icon: UserCheck, t: "Dedicated Investment Guidance" },
  { icon: Globe, t: "Global Investor Friendly" },
  { icon: Sparkles, t: "Personalized Support" },
];
const WHO = ["Individual Investors", "Business Owners", "HNIs", "NRIs", "Family Offices", "Corporate Investors"];
const WHO_ICONS = [UserCheck, Briefcase, TrendingUp, Globe, Landmark, Users];

const WhyWho = () => (
  <section data-testid="invest-whywho" className="border-t border-white/5">
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-24 md:py-32">
      <Reveal><Overline>Why Invest</Overline></Reveal>
      <Reveal delay={0.05}><h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight max-w-2xl">Built for investors who value structure and clarity.</h2></Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-14">
        {WHY.map((c, i) => {
          const Icon = c.icon;
          return (
            <Reveal key={c.t} delay={i * 0.05}>
              <div className="group h-full rounded-3xl border border-white/10 bg-white/[0.02] p-7 hover:-translate-y-1 hover:border-white/20 transition-all duration-500">
                <Icon size={22} strokeWidth={1.5} className="text-neutral-400 group-hover:text-white transition-colors" />
                <h3 className="text-lg font-semibold mt-6 leading-snug">{c.t}</h3>
              </div>
            </Reveal>
          );
        })}
      </div>

      <Reveal><div className="mt-20"><Overline color={TEAL}>Who Can Invest</Overline></div></Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {WHO.map((w, i) => {
          const Icon = WHO_ICONS[i];
          return (
            <Reveal key={w} delay={i * 0.05}>
              <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent p-5">
                <Icon size={20} strokeWidth={1.5} style={{ color: ACCENT }} />
                <p className="font-medium">{w}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- about developer ---------------- */
const AboutDeveloper = () => (
  <section data-testid="invest-about" className="border-t border-white/5">
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-24 md:py-32 grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <Overline>About The Developer</Overline>
        <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight">Nearly four decades of development experience.</h2>
        <div className="mt-8 grid grid-cols-2 gap-y-6 gap-x-4">
          {[["Founded 1987", "Established legacy"], ["38+ Years", "Development experience"], ["NSE & BSE", "Publicly listed"], ["Multi-City", "Presence across India"]].map(([a, b]) => (
            <div key={a}>
              <p className="text-2xl font-semibold" style={{ color: ACCENT }}>{a}</p>
              <p className="text-sm text-neutral-500 mt-1">{b}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-neutral-400 leading-relaxed max-w-lg">Professionally managed with extensive development experience across multiple Indian cities. Investment structure, documentation and applicable terms are shared during consultation.</p>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="rounded-3xl border border-white/10 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1621478493131-8597c4d4f5b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBhcmNoaXRlY3R1cmUlMjBkYXJrfGVufDB8fHx8MTc4NjIyNzUyMXww&ixlib=rb-4.1.0&q=85"
            alt="Modern luxury architecture" loading="lazy" className="w-full h-full object-cover" />
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- faq ---------------- */
const FAQS = [
  ["What is the minimum investment?", "The minimum investment is ₹25,00,000 (₹25 Lakhs). There is no fixed upper limit — larger investments are welcome and discussed during consultation."],
  ["Can NRIs invest?", "Yes. Both Indian residents and NRIs are welcome. Applicable structure, documentation and terms for NRI investment are discussed during your private consultation."],
  ["How does the process work?", "You plan your investment, submit an enquiry, our team reviews it and arranges a private consultation. Documentation follows, then the investment process and ongoing support."],
  ["How is documentation handled?", "All opportunities are supported by professional, transparent documentation. Full details of the investment structure and applicable terms are shared during consultation."],
  ["Can I invest jointly?", "Joint and corporate investments are supported. Please mention this in your enquiry or during consultation so we can guide the appropriate structure."],
  ["How do consultations work?", "Consultations are private and can be conducted over phone, Zoom or an office meeting, based on your preference."],
];
const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="invest-faq" className="border-t border-white/5">
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-24 md:py-32">
        <Reveal><Overline>FAQ</Overline></Reveal>
        <Reveal delay={0.05}><h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight">Questions, answered.</h2></Reveal>
        <div className="mt-12 divide-y divide-white/10 border-t border-white/10">
          {FAQS.map(([q, a], i) => (
            <div key={q} className="py-5">
              <button data-testid={`faq-${i}`} onClick={() => setOpen(open === i ? -1 : i)} className="flex w-full items-center justify-between gap-4 text-left">
                <span className="text-lg font-medium">{q}</span>
                <ChevronDown size={20} className={`flex-none text-neutral-500 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                    <p className="text-neutral-400 leading-relaxed pt-4 pr-8">{a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- final cta + footer ---------------- */
const FinalCta = () => {
  return (
    <section id="final-cta" data-testid="invest-final-cta" className="border-t border-white/5 relative overflow-hidden">
      <img src="https://images.unsplash.com/photo-1540760029765-138c8f6d2eac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmclMjBwcmVtaXVtfGVufDB8fHx8MTc4NjIyNzUyMXww&ixlib=rb-4.1.0&q=85"
        alt="Luxury consultation room" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(8,8,10,0.85), rgba(8,8,10,0.97))" }} />
      <div className="relative max-w-4xl mx-auto px-5 md:px-8 py-28 md:py-40 text-center">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-tight max-w-3xl mx-auto">Ready to explore real estate investment opportunities?</h2>
          <p className="mt-6 text-lg text-neutral-400 max-w-xl mx-auto">Book a confidential consultation with an investment advisor.</p>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="mt-10 flex justify-center">
            <a href={`mailto:${SITE.email}?subject=${encodeURIComponent("Investment consultation request")}`} data-testid="final-book-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-8 py-4 hover:bg-[#D9A441] transition-colors duration-300">
              <Calendar size={16} /> Book Consultation
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer data-testid="invest-footer" className="border-t border-white/5">
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-16">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <p className="font-semibold">Anubhav Sahni</p>
          <a href={`mailto:${SITE.email}`} className="text-sm text-neutral-400 hover:text-white transition-colors inline-flex items-center gap-2 mt-2"><Mail size={14} /> {SITE.email}</a>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-500">
          <Link to="/invest/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/invest/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
          <Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link>
          <a href="#final-cta" className="hover:text-white transition-colors">Investment Disclaimer</a>
        </div>
      </div>
      <p className="mt-8 text-xs text-neutral-600 leading-relaxed max-w-3xl">
        Investment Disclaimer: All figures presented in the Investment Planner are illustrative estimates only and do not constitute guaranteed returns. Investment opportunities are subject to documentation, applicable terms and the final investment structure, which are discussed during consultation.
      </p>
      <p className="mt-4 text-xs text-neutral-600">© {new Date().getFullYear()} Anubhav Sahni · {SITE.city}</p>
    </div>
  </footer>
);

/* ---------------- page ---------------- */
export default function Invest() {
  const [rate, setRate] = useState(15);
  const [rates, setRates] = useState(null);
  const [currency, setCurrency] = useState("INR");
  const [amountInr, setAmountInr] = useState(MIN_INR);
  const [duration, setDuration] = useState(12);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [result, setResult] = useState(null);
  const detected = useRef(false);

  useEffect(() => {
    axios.get(`${API}/invest/settings`).then(({ data }) => { if (data?.annual_rate_pct) setRate(data.annual_rate_pct); }).catch(() => {});
    axios.get(`${API}/invest/rates`).then(({ data }) => { if (data?.rates) setRates(data.rates); }).catch(() => {});
    // currency auto-detect (manual override always wins after)
    axios.get("https://ipapi.co/json/", { timeout: 4000 }).then(({ data }) => {
      if (detected.current) return;
      const cc = (data?.country_code || "").toUpperCase();
      const ccy = COUNTRY_CCY[cc] || (EURO.includes(cc) ? "EUR" : null);
      if (ccy && CURRENCIES[ccy]) { detected.current = true; setCurrency(ccy); }
    }).catch(() => {});
  }, []);

  const onContinue = () => {
    setShowEnquiry(true);
    setTimeout(() => document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth" }), 60);
  };
  const onDone = (r) => {
    setResult(r);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
  };

  const planner = useMemo(() => ({ currency, amountInr, duration }), [currency, amountInr, duration]);

  return (
    <main className="bg-[#08080A] text-[#EDEDED] overflow-x-hidden">
      <Seo />
      <Helmet>
        <title>Invest — Real Estate Investment Opportunities | Anubhav Sahni</title>
        <meta name="description" content="Explore professionally structured real estate investment opportunities with an established, exchange-listed Indian developer. Plan your investment and connect with an advisor." />
      </Helmet>
      <InvestNav />
      {result ? (
        <div className="pt-16"><ThankYou result={result} /><FinalCta /><Footer /></div>
      ) : (
        <>
          <Hero />
          <Trust />
          <Planner
            rate={rate} rates={rates} currency={currency} setCurrency={setCurrency}
            amountInr={amountInr} setAmountInr={setAmountInr} duration={duration} setDuration={setDuration}
            onContinue={onContinue}
          />
          {showEnquiry && <Enquiry open={showEnquiry} planner={planner} rates={rates} rate={rate} onDone={onDone} />}
          <HowItWorks />
          <WhyWho />
          <AboutDeveloper />
          <Faq />
          <FinalCta />
          <Footer />
        </>
      )}
    </main>
  );
}
